import { service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { underscore } from 'frontend-utils/utils/convert-case';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type AjaxService from 'lumen/services/ajax';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface SubscriptionProfileAddress {
  cityName: string;
  country: string;
  number: string;
  postalCode: string;
  street: string;
}

// UNIFIED SETTINGS ADDRESS PROPERTIES.
// IMPORTANT: keep the values in the array AddressProperties, synced with
// interface SubscriptionProfileAddress.
// This array does stores all the properties from SubscriptionProfileAddress
// which are needed to verify if the address was changes in Ember Data Store.
// Used in the method hasValuesChanges.
const AddressProperties: (keyof SubscriptionProfileAddress)[] = [
  'cityName',
  'country',
  'number',
  'postalCode',
  'street',
] as const;

export interface SubscriptionProfileAddressPayload {
  city_name: string;
  country: string;
  number: string;
  postal_code: string;
  street: string;
}

export interface SubscriptionProfilePayload {
  address: SubscriptionProfileAddressPayload;
  brand_url: string;
  email: string[];
  hotel_id: string;
  hotel_name: string;
  language: string;
  latitude: number | null;
  logo_path: string;
  longitude: number | null;
  telnr: string;
  timezone: string;
}

// Attrs that a card can claim ownership of and include in a PATCH payload.
// logoPath is intentionally excluded — logo is managed via dedicated PUT/DELETE endpoints.
// timezone is intentionally excluded — it is not editable in any card yet.
export type SubscriptionProfileOwnedAttrs = Pick<
  SubscriptionProfile,
  | 'address'
  | 'brandUrl'
  | 'email'
  | 'hotelName'
  | 'language'
  | 'latitude'
  | 'longitude'
  | 'telnr'
>;

export default class SubscriptionProfile extends Model {
  [Type] = 'subscription/profile' as const;

  @attr('subscription-profile-address')
  declare address: SubscriptionProfileAddress;

  @attr('string') declare brandUrl: string;

  @attr() declare email: string[];

  @attr('string') declare hotelId: string;

  @attr('string') declare hotelName: string;

  @attr('string') declare language: string;

  @attr('number') declare latitude: number | null;

  @attr('string') declare logoPath: string;

  @attr('number') declare longitude: number | null;

  @attr('string') declare telnr: string;

  @attr('string') declare timezone: string;

  @service declare ajax: AjaxService;

  hasValuesChanges(
    ownedAttrs?: readonly (keyof SubscriptionProfileOwnedAttrs)[],
  ): boolean {
    if (!this.hasDirtyAttributes) {
      return false;
    }
    const changed = this.changedAttributes();
    const relevant = ownedAttrs
      ? Object.keys(changed).filter((attr) =>
          (ownedAttrs as readonly string[]).includes(attr),
        )
      : Object.keys(changed);

    if (relevant.length === 0) {
      return false;
    }

    if (relevant.includes('latitude') || relevant.includes('longitude')) {
      return true;
    }

    if (relevant.includes('address')) {
      const [before, after] = changed['address'] as unknown as [
        SubscriptionProfileAddress | undefined,
        SubscriptionProfileAddress | undefined,
      ];

      if (
        AddressProperties.some(
          (field) => (before?.[field] ?? '') !== (after?.[field] ?? ''),
        )
      ) {
        return true;
      }
    }

    if (relevant.includes('email')) {
      const [before, after] = changed['email'] as unknown as [
        string[] | undefined,
        string[] | undefined,
      ];
      const prevEmails = before ?? [];
      const nextEmails = after ?? [];
      if (
        prevEmails.length !== nextEmails.length ||
        prevEmails.some((email, index) => email !== nextEmails[index])
      ) {
        return true;
      }
    }

    return relevant.some((attr) => attr !== 'address' && attr !== 'email');
  }

  /**
   * Card-scoped PATCH for the My Property settings page.
   *
   * This method steps outside EmberData's `save()` flow and we're
   * not happy about it. The page has independent Save/Cancel per card (Business
   * Profile, Address), so the standard save can't be used — it would persist
   * *all* dirty attrs and `rollbackAttributes()` would discard the other card's
   * pending edits.
   *
   * Instead:
   *   1. Partial-serialize: PATCH body contains only `ownedAttrs` (+ coord pair atomicity).
   *   2. Snapshot non-owned dirty edits before the request.
   *   3. After the store push, rollback to the new canonical, then re-apply the snapshot.
   *
   * After a successful save:
   *   - owned attrs are clean (match the server response)
   *   - non-owned dirty attrs are preserved (the other card stays editable)
   *
   * Future cleanup: better designs exist — a single Save button for the whole page
   * (allows using `profile.save()` directly) or splitting the two cards into separate
   * EmberData models (each gets its own native save). Either would remove this method
   * and the matching per-attr cancel loops in card-business-profile and card-address.
   *
   * We're shipping the custom approach now because of project deadlines on Unified
   * Settings — pivoting to a less custom design isn't on the table for this iteration.
   * Revisit when the deadline pressure is off.
   */
  async saveForSubscription(
    subscriptionId: string,
    ownedAttrs: readonly (keyof SubscriptionProfileOwnedAttrs)[],
  ): Promise<SubscriptionProfile> {
    const changed = this.changedAttributes();

    // PATCH ATOMICITY CONTRACT: if a card owns either coordinate, treat both as
    // owned for restore purposes too — otherwise the non-listed coordinate would
    // be re-applied on top of the server's confirmed value, undoing the atomic save.
    const effectiveOwnedAttrs = new Set<string>(
      ownedAttrs as readonly string[],
    );
    if (
      effectiveOwnedAttrs.has('latitude') ||
      effectiveOwnedAttrs.has('longitude')
    ) {
      effectiveOwnedAttrs.add('latitude').add('longitude');
    }

    const pendingEditsToRestore: [string, unknown][] = Object.entries(changed)
      .filter(([attrName]) => !effectiveOwnedAttrs.has(attrName))
      .map(([attrName, [, pendingValue]]) => [attrName, pendingValue]);

    const fullSerialized = this.serialize() as Record<string, unknown>;

    let body: Record<string, unknown>;
    if (this.isNew) {
      body = fullSerialized;
    } else {
      body = Object.fromEntries(
        [...effectiveOwnedAttrs].map((attrName) => {
          const payloadKey = underscore(attrName);
          return [payloadKey, fullSerialized[payloadKey]];
        }),
      );
    }

    const url = formatUrl(
      `/${APIGATEWAY}/configure/subscription/profile`,
      { subscription_id: subscriptionId },
      this.ajax.host,
    );

    const response: SubscriptionProfilePayload = await this.ajax.request(url, {
      data: JSON.stringify(body),
      method: this.isNew ? 'POST' : 'PATCH',
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
    });

    const normalisedResponse = pushAndReturnPayload(
      this.store,
      { 'subscription-profiles': [{ ...response, id: subscriptionId }] },
      'subscription/profile',
    );

    if (!this.isNew) {
      this.rollbackAttributes();

      for (const [attr, value] of pendingEditsToRestore) {
        // Cast needed to set EmberData attrs dynamically without per-attr type guards.
        (this as unknown as Record<string, unknown>)[attr] = value;
      }
    }

    return (
      normalisedResponse['subscription-profiles'] as SubscriptionProfile[]
    )[0]!;
  }
}
